/**
 * Wizard Steg 2.5: Kassaflödesanalys (K3-obligatorisk)
 *
 * Visar den automatiskt beräknade kassaflödesanalysen enligt indirekt metod.
 * Användaren kan göra manuella justeringar för poster som inte kan beräknas
 * automatiskt från SIE-datan.
 *
 * Enligt BFNAR 2012:1 (K3) kapitel 7 är kassaflödesanalysen obligatorisk.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  Plus,
  Trash2,
  Repeat,
  Building2,
  Landmark,
} from "lucide-react";
import {
  reportsApi,
  wizardApi,
  type AnnualReport,
  type CashFlowStatement,
  type CashFlowManualAdjustment,
} from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { toast } from "sonner";

interface Step2_5CashFlowProps {
  report: AnnualReport;
  onUpdate: (report: AnnualReport) => void;
  /** Override save (for guest/public wizard) */
  onSaveInput?: (data: Record<string, unknown>) => Promise<{ success: boolean; report?: AnnualReport; error?: string }>;
  /** Override navigation (for guest/public wizard or K3 decimal steps) */
  onNavigateStep?: (step: number | string) => void;
  /** Previous step (default: "2") */
  prevStep?: number | string;
  /** Next step (default: "3") */
  nextStep?: number | string;
}

// Helper to format currency
function formatCurrency(amount: number | undefined | null): string {
  if (amount == null) return "0";
  return formatNumber(amount);
}


export function Step2_5CashFlow({
  report,
  onUpdate,
  onSaveInput,
  onNavigateStep,
  prevStep = 2,
  nextStep = 3,
}: Step2_5CashFlowProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [cashFlow, setCashFlow] = useState<CashFlowStatement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check if we're in guest mode (when onNavigateStep is provided)
  const isGuestMode = !!onNavigateStep;

  // Manual adjustments state
  const [operatingAdjustments, setOperatingAdjustments] = useState<CashFlowManualAdjustment[]>([]);
  const [investingAdjustments, setInvestingAdjustments] = useState<CashFlowManualAdjustment[]>([]);
  const [financingAdjustments, setFinancingAdjustments] = useState<CashFlowManualAdjustment[]>([]);

  // Load cash flow data
  useEffect(() => {
    loadCashFlow();
  }, [report.id, isGuestMode]);

  const loadCashFlow = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // In guest mode, check if cash flow is already in userInput, otherwise calculate from SIE data
      if (isGuestMode) {
        // Check if cash flow was already saved in userInput
        const existingCashFlow = (report.userInput as Record<string, unknown>)?.cashFlow as CashFlowStatement | undefined;
        if (existingCashFlow) {
          setCashFlow(existingCashFlow);
          setOperatingAdjustments(existingCashFlow.operatingActivities?.manualAdjustments || []);
          setInvestingAdjustments(existingCashFlow.investingActivities?.manualAdjustments || []);
          setFinancingAdjustments(existingCashFlow.financingActivities?.manualAdjustments || []);
          setIsLoading(false);
          return;
        }

        // Otherwise, calculate cash flow from SIE data using the public wizard API
        if (!report.parsedSieData) {
          setError("Ingen SIE-data tillgänglig för kassaflödesberäkning");
          setIsLoading(false);
          return;
        }

        const response = await wizardApi.calculateCashFlow({
          parsedSieData: report.parsedSieData,
          userInput: report.userInput as Record<string, unknown>,
        });

        if (response.success && response.data) {
          setCashFlow(response.data.cashFlow);
          setOperatingAdjustments(response.data.cashFlow?.operatingActivities?.manualAdjustments || []);
          setInvestingAdjustments(response.data.cashFlow?.investingActivities?.manualAdjustments || []);
          setFinancingAdjustments(response.data.cashFlow?.financingActivities?.manualAdjustments || []);
        } else {
          setError(response.error?.message || "Kunde inte beräkna kassaflödesanalysen");
        }
      } else {
        // In authenticated mode, use the reports API
        const response = await reportsApi.getCashFlow(report.id);
        if (response.success && response.data) {
          setCashFlow(response.data.cashFlow);
          setOperatingAdjustments(response.data.cashFlow?.operatingActivities?.manualAdjustments || []);
          setInvestingAdjustments(response.data.cashFlow?.investingActivities?.manualAdjustments || []);
          setFinancingAdjustments(response.data.cashFlow?.financingActivities?.manualAdjustments || []);
        } else {
          setError(response.error?.message || "Kunde inte ladda kassaflödesanalysen");
        }
      }
    } catch (err) {
      console.error("Load cash flow error:", err);
      setError("Ett fel uppstod vid laddning av kassaflödesanalysen");
    } finally {
      setIsLoading(false);
    }
  };

  // Save cash flow with adjustments
  const saveCashFlow = useCallback(async () => {
    if (!cashFlow) return;

    setIsSaving(true);
    try {
      const updatedCashFlow = {
        ...cashFlow,
        operatingActivities: {
          ...cashFlow.operatingActivities,
          manualAdjustments: operatingAdjustments,
        },
        investingActivities: {
          ...cashFlow.investingActivities,
          manualAdjustments: investingAdjustments,
        },
        financingActivities: {
          ...cashFlow.financingActivities,
          manualAdjustments: financingAdjustments,
        },
        userAdjusted: true,
      };

      const response = await reportsApi.updateCashFlow(report.id, updatedCashFlow);
      if (response.success && response.data) {
        setCashFlow(response.data.cashFlow);
        // Preserve the company relation which may not be included in the API response
        onUpdate({ ...response.data.report, company: report.company });
        toast.success("Kassaflödesanalysen har sparats");
      } else {
        toast.error(response.error?.message || "Kunde inte spara");
      }
    } catch (err) {
      console.error("Save cash flow error:", err);
      toast.error("Ett fel uppstod vid sparande");
    } finally {
      setIsSaving(false);
    }
  }, [cashFlow, operatingAdjustments, investingAdjustments, financingAdjustments, report.id, onUpdate]);

  // Add manual adjustment
  const addAdjustment = (
    section: "operating" | "investing" | "financing",
    adjustment: CashFlowManualAdjustment
  ) => {
    switch (section) {
      case "operating":
        setOperatingAdjustments([...operatingAdjustments, adjustment]);
        break;
      case "investing":
        setInvestingAdjustments([...investingAdjustments, adjustment]);
        break;
      case "financing":
        setFinancingAdjustments([...financingAdjustments, adjustment]);
        break;
    }
  };

  // Remove manual adjustment
  const removeAdjustment = (section: "operating" | "investing" | "financing", index: number) => {
    switch (section) {
      case "operating":
        setOperatingAdjustments(operatingAdjustments.filter((_, i) => i !== index));
        break;
      case "investing":
        setInvestingAdjustments(investingAdjustments.filter((_, i) => i !== index));
        break;
      case "financing":
        setFinancingAdjustments(financingAdjustments.filter((_, i) => i !== index));
        break;
    }
  };

  // Navigation
  const goToPrevStep = () => {
    window.scrollTo(0, 0);
    if (onNavigateStep) {
      onNavigateStep(prevStep);
    } else {
      navigate(`/dashboard/wizard/${report.id}/step/${prevStep}`);
    }
  };

  const goToNextStep = async () => {
    // In guest mode, save via onSaveInput; otherwise use API
    if (isGuestMode && onSaveInput && cashFlow) {
      setIsSaving(true);
      try {
        const updatedCashFlow = {
          ...cashFlow,
          operatingActivities: {
            ...cashFlow.operatingActivities,
            manualAdjustments: operatingAdjustments,
          },
          investingActivities: {
            ...cashFlow.investingActivities,
            manualAdjustments: investingAdjustments,
          },
          financingActivities: {
            ...cashFlow.financingActivities,
            manualAdjustments: financingAdjustments,
          },
          userAdjusted: true,
        };
        await onSaveInput({ cashFlow: updatedCashFlow });
        onNavigateStep!(nextStep);
      } catch (err) {
        console.error("Save cash flow error:", err);
        toast.error("Ett fel uppstod vid sparande");
      } finally {
        setIsSaving(false);
      }
    } else {
      await saveCashFlow();
      // Also update the current step in the report
      try {
        const stepNum = typeof nextStep === "string" ? parseFloat(nextStep) : nextStep;
        await reportsApi.updateStep(report.id, stepNum);
      } catch {
        // Ignore step update errors
      }
      navigate(`/dashboard/wizard/${report.id}/step/${nextStep}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Beräknar kassaflödesanalys...</p>
        </div>
      </div>
    );
  }

  if (error || !cashFlow) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-4" />
        <p className="text-red-700 mb-4">{error || "Kunde inte beräkna kassaflödesanalysen"}</p>
        <button onClick={loadCashFlow} className="btn btn-outline">
          Försök igen
        </button>
      </div>
    );
  }

  const { operatingActivities, investingActivities, financingActivities, summary } = cashFlow;

  // Calculate totals with adjustments
  const operatingAdjustmentTotal = operatingAdjustments.reduce((sum, a) => sum + a.amount, 0);
  const investingAdjustmentTotal = investingAdjustments.reduce((sum, a) => sum + a.amount, 0);
  const financingAdjustmentTotal = financingAdjustments.reduce((sum, a) => sum + a.amount, 0);

  const adjustedOperatingTotal = operatingActivities.total + operatingAdjustmentTotal;
  const adjustedInvestingTotal = investingActivities.total + investingAdjustmentTotal;
  const adjustedFinancingTotal = financingActivities.total + financingAdjustmentTotal;
  const adjustedNetCashFlow = adjustedOperatingTotal + adjustedInvestingTotal + adjustedFinancingTotal;

  // Check if cash flow balances
  const calculatedClosingCash = summary.openingCash + adjustedNetCashFlow + summary.exchangeRateDifference;
  const balancesDifference = Math.abs(calculatedClosingCash - summary.closingCash);
  const balancesCorrectly = balancesDifference < 1;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Kassaflödesanalys</h2>
        <p className="text-gray-600">
          Granska den automatiskt beräknade analysen och gör eventuella justeringar.
        </p>
      </div>

      {/* Balance status */}
      <div
        className={`flex items-center gap-3 p-4 rounded-lg border ${
          balancesCorrectly
            ? "bg-green-50 border-green-200"
            : "bg-amber-50 border-amber-200"
        }`}
      >
        {balancesCorrectly ? (
          <>
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">
              Kassaflödesanalysen balanserar korrekt mot förändringen i likvida medel.
            </span>
          </>
        ) : (
          <>
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="text-amber-700">
              Kassaflödesanalysen balanserar inte. Differens: {formatCurrency(balancesDifference)} kr.
              Kontrollera manuella justeringar.
            </span>
          </>
        )}
      </div>

      {/* Method info */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium">Indirekt metod</p>
          <p className="mt-1">
            Kassaflödesanalysen är beräknad enligt den indirekta metoden där rörelseresultatet justeras för
            poster som inte är kassapåverkande samt förändringar i rörelsekapital.
          </p>
        </div>
      </div>

      {/* Cash Flow Sections */}
      <div className="grid gap-6">
        {/* Operating Activities */}
        <CashFlowSection
          title="Den löpande verksamheten"
          icon={<Repeat size={20} />}
          items={[
            { label: "Rörelseresultat", value: operatingActivities.operatingResult },
            ...(operatingActivities.adjustments
              ? [
                  { label: "Av- och nedskrivningar", value: operatingActivities.adjustments.depreciation, indent: true },
                  { label: "Övriga justeringar", value: operatingActivities.adjustments.otherNonCashItems, indent: true },
                ]
              : []),
            ...(operatingActivities.workingCapitalChanges
              ? [
                  { label: "Förändring varulager", value: operatingActivities.workingCapitalChanges.inventory, indent: true },
                  { label: "Förändring fordringar", value: operatingActivities.workingCapitalChanges.receivables, indent: true },
                  { label: "Förändring skulder", value: operatingActivities.workingCapitalChanges.payables, indent: true },
                ]
              : []),
            { label: "Betald skatt", value: operatingActivities.taxPaid, indent: true },
          ]}
          adjustments={operatingAdjustments}
          onAddAdjustment={(adj) => addAdjustment("operating", adj)}
          onRemoveAdjustment={(idx) => removeAdjustment("operating", idx)}
          total={adjustedOperatingTotal}
          autoCalculatedTotal={operatingActivities.total}
        />

        {/* Investing Activities */}
        <CashFlowSection
          title="Investeringsverksamheten"
          icon={<Building2 size={20} />}
          items={[
            { label: "Förvärv av materiella tillgångar", value: investingActivities.acquisitionOfTangibleAssets },
            { label: "Försäljning av materiella tillgångar", value: investingActivities.disposalOfTangibleAssets },
            { label: "Förvärv av immateriella tillgångar", value: investingActivities.acquisitionOfIntangibleAssets },
            { label: "Förvärv av finansiella tillgångar", value: investingActivities.acquisitionOfFinancialAssets },
            { label: "Försäljning av finansiella tillgångar", value: investingActivities.disposalOfFinancialAssets },
          ]}
          adjustments={investingAdjustments}
          onAddAdjustment={(adj) => addAdjustment("investing", adj)}
          onRemoveAdjustment={(idx) => removeAdjustment("investing", idx)}
          total={adjustedInvestingTotal}
          autoCalculatedTotal={investingActivities.total}
        />

        {/* Financing Activities */}
        <CashFlowSection
          title="Finansieringsverksamheten"
          icon={<Landmark size={20} />}
          items={[
            { label: "Nyemission", value: financingActivities.newShareIssue },
            { label: "Upptagna lån", value: financingActivities.newLoans },
            { label: "Amortering av lån", value: financingActivities.repaymentOfLoans },
            { label: "Utbetald utdelning", value: financingActivities.dividendPaid },
            { label: "Koncernbidrag", value: financingActivities.groupContributions },
            { label: "Aktieägartillskott", value: financingActivities.shareholderContributions },
          ]}
          adjustments={financingAdjustments}
          onAddAdjustment={(adj) => addAdjustment("financing", adj)}
          onRemoveAdjustment={(idx) => removeAdjustment("financing", idx)}
          total={adjustedFinancingTotal}
          autoCalculatedTotal={financingActivities.total}
        />

        {/* Summary */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-green-600" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Sammanfattning</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Kassaflöde från löpande verksamhet</span>
              <span className={`font-medium ${adjustedOperatingTotal >= 0 ? "text-green-600" : "text-red-600"}`}>
                {formatCurrency(adjustedOperatingTotal)} kr
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Kassaflöde från investeringsverksamhet</span>
              <span className={`font-medium ${adjustedInvestingTotal >= 0 ? "text-green-600" : "text-red-600"}`}>
                {formatCurrency(adjustedInvestingTotal)} kr
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Kassaflöde från finansieringsverksamhet</span>
              <span className={`font-medium ${adjustedFinancingTotal >= 0 ? "text-green-600" : "text-red-600"}`}>
                {formatCurrency(adjustedFinancingTotal)} kr
              </span>
            </div>
            <hr className="border-gray-300" />
            <div className="flex justify-between items-center text-lg font-semibold">
              <span className="text-gray-900">Årets kassaflöde</span>
              <span className={adjustedNetCashFlow >= 0 ? "text-green-600" : "text-red-600"}>
                {formatCurrency(adjustedNetCashFlow)} kr
              </span>
            </div>
            <hr className="border-gray-300" />
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Likvida medel vid årets början</span>
              <span className="font-medium">{formatCurrency(summary.openingCash)} kr</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Kursdifferens i likvida medel</span>
              <span className="font-medium">{formatCurrency(summary.exchangeRateDifference)} kr</span>
            </div>
            <div className="flex justify-between items-center text-lg font-semibold">
              <span className="text-gray-900">Likvida medel vid årets slut</span>
              <span>{formatCurrency(summary.closingCash)} kr</span>
            </div>
            {!balancesCorrectly && (
              <div className="mt-2 p-2 bg-amber-100 rounded text-sm text-amber-700">
                Beräknad utgående kassa: {formatCurrency(calculatedClosingCash)} kr
                (differens: {formatCurrency(balancesDifference)} kr)
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button onClick={goToPrevStep} className="btn btn-secondary flex items-center">
          <ArrowLeft size={20} className="mr-2" />
          Tillbaka
        </button>
        <button
          onClick={goToNextStep}
          disabled={isSaving}
          className="btn btn-primary flex items-center"
        >
          {isSaving ? (
            <>
              <Loader2 size={20} className="mr-2 animate-spin" />
              Sparar...
            </>
          ) : (
            <>
              Fortsätt
              <ArrowRight size={20} className="ml-2" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Cash Flow Section Component
interface CashFlowSectionProps {
  title: string;
  icon: React.ReactNode;
  items: Array<{ label: string; value?: number; indent?: boolean }>;
  adjustments: CashFlowManualAdjustment[];
  onAddAdjustment: (adj: CashFlowManualAdjustment) => void;
  onRemoveAdjustment: (index: number) => void;
  total: number;
  autoCalculatedTotal: number;
}

function CashFlowSection({
  title,
  icon,
  items,
  adjustments,
  onAddAdjustment,
  onRemoveAdjustment,
  total,
  autoCalculatedTotal,
}: CashFlowSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const [newAmount, setNewAmount] = useState("");

  const handleAdd = () => {
    if (!newDescription.trim() || !newAmount) return;
    onAddAdjustment({
      description: newDescription.trim(),
      amount: parseFloat(newAmount),
    });
    setNewDescription("");
    setNewAmount("");
    setShowAddForm(false);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">{icon}</div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="text-right">
          <span className={`text-lg font-semibold ${total >= 0 ? "text-green-600" : "text-red-600"}`}>
            {formatCurrency(total)} kr
          </span>
          {adjustments.length > 0 && (
            <p className="text-xs text-gray-500">
              (auto: {formatCurrency(autoCalculatedTotal)} kr)
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {/* Auto-calculated items */}
        {items
          .filter((item) => item.value != null && item.value !== 0)
          .map((item, idx) => (
            <div
              key={idx}
              className={`flex justify-between items-center py-1.5 ${
                item.indent ? "pl-4 text-sm text-gray-600" : "text-gray-700"
              }`}
            >
              <span>{item.label}</span>
              <span className={`tabular-nums font-medium ${
                item.value! > 0 ? "text-green-600" : item.value! < 0 ? "text-red-600" : "text-gray-600"
              }`}>
                {formatCurrency(item.value)} kr
              </span>
            </div>
          ))}

        {/* Manual adjustments */}
        {adjustments.length > 0 && (
          <>
            <hr className="my-2 border-gray-200" />
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Manuella justeringar</p>
            {adjustments.map((adj, idx) => (
              <div key={idx} className="flex justify-between items-center py-1.5 pl-4 text-sm">
                <span className="text-gray-600">{adj.description}</span>
                <div className="flex items-center gap-3">
                  <span className={`tabular-nums font-medium ${
                    adj.amount > 0 ? "text-green-600" : adj.amount < 0 ? "text-red-600" : "text-gray-600"
                  }`}>
                    {formatCurrency(adj.amount)} kr
                  </span>
                  <button
                    onClick={() => onRemoveAdjustment(idx)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Add adjustment form */}
        {showAddForm ? (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3">
            <input
              type="text"
              placeholder="Beskrivning"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Belopp (negativa för utflöden)"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <button
                onClick={handleAdd}
                disabled={!newDescription.trim() || !newAmount}
                className="btn btn-sm btn-primary"
              >
                Lägg till
              </button>
              <button onClick={() => setShowAddForm(false)} className="btn btn-sm btn-outline">
                Avbryt
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="mt-2 flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
          >
            <Plus className="w-4 h-4" />
            Lägg till justering
          </button>
        )}
      </div>
    </div>
  );
}
